import React, { forwardRef, useImperativeHandle } from 'react';
import { ref } from 'yup';

import Modal from '@/components/Modal';
import { CustomAmountInput } from '@/components/Modals/TopUpModal/CustomAmountInput';
import { OptionButton } from '@/components/Modals/TopUpModal/OptionButton';
import { useTopUpModal } from '@/components/Modals/TopUpModal/useTopUpModal';

export interface TopUpModalRef {
  openModal: () => void;
  closeModal: () => void;
}

const TopUpModal = forwardRef<TopUpModalRef>((_, ref) => {
  const [isOpen, setIsOpen] = React.useState(false);

  useImperativeHandle(ref, () => ({
    openModal: () => setIsOpen(true),
    closeModal: () => setIsOpen(false),
  }));

  const {
    user,
    topUpError,
    isTopUpLoading,
    formattedCustomAmountInputValue,
    minDepositFormatted,
    selectedAmountOption,
    amountOptions,
    showMinimumTopUpAmountWarning,
    isCustomAmountOptionSelected,
    handleTopUpClick,
    handleCustomAmountInputChange,
    handleSelectAmountOption,
    handleSelectCustomAmountOption,
  } = useTopUpModal();

  return !user ? (
    <></>
  ) : (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => setIsOpen(false)}
      className='md:max-w-500px p-16 box-border relative gap-10'
    >
      <div className='flex flex-col items-start gap-10 h-max-content'>
        <h2
          className='font-bold text-18px font-serif text-customBlack'
          style={{ letterSpacing: '-1px' }}
        >
          Top Up
        </h2>

        <p className='text-16px font-serif leading-10 text-black'>
          Please enter the amount you want to put to your balance. This balance
          will be used for future booking transactions.
        </p>

        {/* Predefined options */}
        {amountOptions.map((option) => (
          <OptionButton
            key={option.id}
            label={option.value}
            isSelected={option.id === selectedAmountOption?.id}
            handleSelect={() => handleSelectAmountOption(option)}
          />
        ))}

        {/* Custom option */}
        <OptionButton
          label={'Custom Amount'}
          isSelected={isCustomAmountOptionSelected}
          handleSelect={handleSelectCustomAmountOption}
        />

        {(isCustomAmountOptionSelected || !!topUpError) && (
          <div className='w-full'>
            {isCustomAmountOptionSelected && (
              <CustomAmountInput
                value={formattedCustomAmountInputValue}
                handleChange={handleCustomAmountInputChange}
                isError={!!topUpError}
              />
            )}

            {!!topUpError && (
              <p className='text-customRed mt-2'>{topUpError}</p>
            )}
          </div>
        )}

        {showMinimumTopUpAmountWarning && minDepositFormatted && (
          <div
            className='flex items-center p-5 rounded-lg'
            style={{ backgroundColor: '#F8FAFC' }}
          >
            <img src='/icons/info.svg' alt=' ' className='mr-4' />
            <span className='text-14px rounded-lg'>
              For your current plan, the minimum top-up amount is{' '}
              <span className='text-customBlue font-bold'>
                {minDepositFormatted}
              </span>
            </span>
          </div>
        )}

        <button
          onClick={handleTopUpClick}
          disabled={isTopUpLoading}
          className='btn w-full flex-shrink-0'
        >
          {isTopUpLoading ? 'Processing...' : 'Top Up'}
        </button>
      </div>
    </Modal>
  );
});

TopUpModal.displayName = 'TopUpModal';

export default TopUpModal;
